import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Form, Hidden, Input } from '@alxgrn/react-form';
import type { FormData } from '@alxgrn/react-form';
import { post } from '../utils/api';
import { useAppDispatch, useAppSelector } from '../hooks/store';
import { authLogin } from '../store/authSlice';
import { Attendee } from '../types';
import { useNavigate } from 'react-router-dom';
import logo from '../components/common/default.png';
import './Login.css';
import getFileURL from '../utils/getFileURL';
import LongLangSwitcher from '../components/login/LongLangSwitcher';
import About from '../modal/About';

type AuthResponse = {
    token: string,
    attendee: Attendee,
};

const Login = () => {
	const { t, i18n } = useTranslation();
	const lang = i18n.language === 'en' ? 'en' : 'ru';
    const name = i18n.language === 'en' ? 'name_en' : 'name_ru';
    const event = useAppSelector(store => store.event);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [ email, setEmail ] = useState('');
    const [ password, setPassword ] = useState('');
    const [ error, setError ] = useState('');
    const [ success, setSuccess ] = useState('');
    const [ isLoading, setIsLoading ] = useState(false);
    const [ isAboutOpen, setIsAboutOpen ] = useState(false);

    const onSubmit = async (data: FormData) => {
		setIsLoading(true);
        setSuccess('');
        setError('');
		const result = await post('/attendees/login', data);
		if(result.error) {
			setError(result.error.text[lang]);
		} 
        if(result.data) {
            const data = result.data as AuthResponse;
			dispatch(authLogin({ token: data.token, user: data.attendee}));
			axios.defaults.headers.common['Authorization'] = 'Bearer ' + data.token;
            setSuccess(data.attendee ? data.attendee[name] : '');
            navigate('..');
		}
        setIsLoading(false);
	};

    if(!event) return (
        <div className='Page'>
            <h2>{t('login.title')}</h2>
            <p className='error'>{t('common.noevent')}</p>
        </div>
    );

	return (
		<div className='Login'>
            <div className='Logos'>
                <div className='Logo'>
                    <img className='Icon' alt='logo' src={logo}/>
                    <div className='Text'><span>Retail</span><br/><span>Connect</span></div>
                </div>
                <div className='Logo'>
                    <img className='Icon' alt='logo' src={getFileURL(event.logo)}/>
                    <div className='Text'>{event[name]}</div>
                </div>
            </div>
            <div className='Title'>
	    	    <div className='H1'>{t('login.title')}</div>
                <div className='Lang'><LongLangSwitcher/></div>
            </div>
            <div className='LoginForm'>
                <Form
                    info={isLoading ? t('common.loading') : undefined}
                    success={success}
                    error={error}
                    submit={t('login.submit')}
                    onSubmit={onSubmit}
                >
                    <Hidden id='event_id' value={event.id}/>
                    <Input
                        id='email'
                        required={true}
                        value={email}
                        onChange={setEmail}
                        label={t('login.email.label')}
                        placeholder={t('login.email.placeholder')}
                        disabled={isLoading}
                    />
                    <Input
                        id='password'
                        type='password'
                        required={true}
                        value={password}
                        onChange={setPassword}
                        label={t('login.password.label')}
                        placeholder={t('login.password.placeholder')}
                        disabled={isLoading}
                    />
                </Form>
                <div className='People'/>
            </div>
            {event.link_recovery && 
            <a className='Link' target='_blank' href={event.link_recovery} rel='noreferrer'>
                {t('login.forgot')}
            </a>}
            <div className='MobileLangSwitcher'>
                <LongLangSwitcher/>
            </div>
            <div className='Footer'>
                {event.link_privacy && <>
                <a target='_blank' href={event.link_privacy} rel='noreferrer'>{t('common.privacy')}</a>&nbsp;&nbsp;|&nbsp;&nbsp;</>}
                <span onClick={() => setIsAboutOpen(true)}>
                    {t('common.about')}
                </span>
            </div>
            <About isOpen={isAboutOpen} onClose={() => setIsAboutOpen(false)}/>
		</div>
	);
};

export default Login;
